/**
 * SuperAuditActivityCard — real-time awareness of super-admin actions.
 *
 * Renders the most recent hits on `require_super_admin`-protected endpoints,
 * highlighting DENIED attempts in red. Piggybacks on the `super_audit_logs`
 * collection written by /app/backend/core/auth.py::require_super_admin.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.round((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const shortPath = (p) => {
  if (!p) return '';
  const clean = p.replace(/^\/api\//, '/');
  if (clean.length <= 42) return clean;
  return clean.slice(0, 40) + '…';
};

const SuperAuditActivityCard = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['super-audit-widget'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/audit-logs?limit=10`, {
        headers: authHeaders(),
      });
      if (r.status === 403) return { items: [], total: 0, forbidden: true };
      if (!r.ok) throw new Error('Failed to load audit');
      return r.json();
    },
    refetchInterval: 30000,
    retry: false,
  });

  if (data?.forbidden) return null; // not a super admin — hide silently

  const items = data?.items || [];
  const deniedCount = items.filter((i) => !i.allowed).length;

  return (
    <Card className="border-slate-200" data-testid="super-audit-activity-card">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-indigo-600" />
          <CardTitle className="text-sm font-semibold">Recent super-admin activity</CardTitle>
          {deniedCount > 0 && (
            <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]" data-testid="audit-denied-count">
              {deniedCount} denied
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-slate-500 hover:text-slate-700"
          onClick={() => navigate('/admin/rbac-audit')}
          data-testid="audit-view-all-btn"
        >
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="py-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="text-xs text-slate-500 py-3 text-center">Could not load activity</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 text-center">No super-admin activity yet</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((row, i) => (
              <li
                key={i}
                className={`flex items-center gap-3 py-2 text-xs ${
                  row.allowed ? '' : 'bg-rose-50/40 -mx-3 px-3 rounded'
                }`}
                data-testid={`audit-row-${i}`}
              >
                {row.allowed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-rose-600 shrink-0" data-testid={`audit-denied-${i}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 truncate">
                    <span className="font-medium truncate">
                      {row.admin_email || 'unknown'}
                    </span>
                    <span className="text-slate-400 text-[10px] uppercase font-mono">{row.method}</span>
                  </div>
                  <div className="text-slate-500 font-mono text-[10px] truncate">{shortPath(row.path)}</div>
                </div>
                <span className="text-[10px] text-slate-400 shrink-0">{relTime(row.timestamp)}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default SuperAuditActivityCard;
